import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '../services/auth'

export interface ShopDetails {
  id: number
  name: string
  shop_owner: string
  email: string
  customer_email: string
  created_at: string
  country: string
  country_code: string
  country_name: string
  currency: string
  domain: string
  money_format: string
  money_with_currency_format: string
  weight_unit: string
  myshopify_domain: string
  plan_name: string
  plan_display_name: string
  timezone: string
  iana_timezone: string
  install_date: string
  jti: string
  install_status: string
  crisp_id: string
  scopes: string[]
  statsig: {
    installDate: string
  }
  subscription_status?: string
  owned_account?: string
}

export const useShopStore = defineStore('shop', () => {
  // State
  const shopDetails = ref<ShopDetails | null>(null)
  const isLoading = ref(false)

  // Caching for shop details
  const hasShopDetailsFetched = ref(false)
  const lastShopDetailsFetch = ref<number>(0)
  const SHOP_DETAILS_CACHE_TTL = 5 * 60 * 1000 // 5 minutes cache
  let shopDetailsDebounceTimeout: NodeJS.Timeout | null = null
  const SHOP_DETAILS_DEBOUNCE_DELAY = 1000 // 1 second debounce

  // Track an in-flight fetch to prevent overlapping calls
  type ShopFetchResult =
    | { ok: true; status: 'ok' }
    | { ok: false; status: 'uninstalled' }
    | { ok: false; status: 'unauthorized' }
    | { ok: false; status: 'error' }

  let shopDetailsInFlight: Promise<ShopFetchResult> | null = null

  // Computed
  const isInstallCompleted = computed(() => {
    return shopDetails.value?.install_status === 'completed'
  })

  const isInstallInProgress = computed(() => {
    return shopDetails.value?.install_status === 'in_progress'
  })

  const hasShopToken = computed(() => {
    return !!authService.getShopToken()
  })

  // Actions
  const fetchShopDetails = async (opts: { force?: boolean; debounce?: boolean } = {}): Promise<ShopFetchResult> => {
    const force = opts.force ?? false
    const debounce = opts.debounce ?? true

    if (!authService.isAuthenticated()) {
      return { ok: false, status: 'unauthorized' }
    }

    const now = Date.now()

    // If we have cached data that's still fresh and not forcing, return it
    if (!force && hasShopDetailsFetched.value && now - lastShopDetailsFetch.value < SHOP_DETAILS_CACHE_TTL) {
      return { ok: true, status: 'ok' }
    }

    // Always reuse an in-flight request to prevent duplicate API calls
    // Even with force: true, we should wait for the existing request rather than making duplicates
    if (shopDetailsInFlight) {
      return shopDetailsInFlight
    }

    // Use delay based on force and debounce options
    const delay = force ? 0 : debounce ? SHOP_DETAILS_DEBOUNCE_DELAY : 0

    shopDetailsInFlight = new Promise((resolve) => {
      shopDetailsDebounceTimeout = setTimeout(async () => {
        isLoading.value = true
        try {
          // Import API service dynamically to avoid circular dependencies
          const { apiService } = await import('../services/api')

          // Fetch shop details
          const shopData = await apiService.getShopDetails()
          shopDetails.value = shopData
          hasShopDetailsFetched.value = true
          // Fix TTL bug: set timestamp AFTER successful fetch, not before delay
          lastShopDetailsFetch.value = Date.now()

          // Check if shop is uninstalled - return status, don't navigate
          if (shopData.install_status === 'uninstalled') {
            resolve({ ok: false, status: 'uninstalled' })
            return
          }

          resolve({ ok: true, status: 'ok' })
        } catch (error: any) {
          // Check for 401/403 status codes - return status, don't navigate
          const msg = String(error?.message ?? '')
          if (msg.includes('401') || msg.includes('403')) {
            resolve({ ok: false, status: 'unauthorized' })
            return
          }
          resolve({ ok: false, status: 'error' })
        } finally {
          isLoading.value = false
          shopDetailsInFlight = null
        }
      }, delay)
    })

    return shopDetailsInFlight
  }

  const completeInstall = async (): Promise<void> => {
    if (!authService.isAuthenticated()) {
      throw new Error('User must be authenticated to complete install')
    }

    // Import API service dynamically to avoid circular dependencies
    const { apiService } = await import('../services/api')

    // Call the complete install API
    await apiService.completeInstall()

    // Update local shop details to reflect completed status
    if (shopDetails.value) {
      shopDetails.value.install_status = 'completed'
    }

    // Clear cache to force refresh on next fetch
    hasShopDetailsFetched.value = false
    lastShopDetailsFetch.value = 0
  }

  const clearShopDetails = () => {
    shopDetails.value = null
    hasShopDetailsFetched.value = false
    lastShopDetailsFetch.value = 0
  }

  return {
    // State
    shopDetails,
    isLoading,

    // Computed
    isInstallCompleted,
    isInstallInProgress,
    hasShopToken,

    // Actions
    fetchShopDetails,
    completeInstall,
    clearShopDetails,
  }
})
